import { debounce } from 'lodash';
import { useCallback, useEffect, useState } from 'react';

import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import {
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
} from '@mui/material';

import { hasPermission } from 'src/utils/has-permissions';

import { useAuthContext } from 'src/auth/hooks';
import { AuthPermissions } from 'src/auth/permissions';

import Label from 'src/components/label';
import Iconify from 'src/components/iconify';

import {
  EMPLOYEE_STATUS_OPTIONS,
  EMPLOYEE_EPF_STATUS_OPTIONS,
  IEmployeeBaseSalaryRange,
  IEmployeeTableFilters,
  IEmployeeTableFilterValue,
} from 'src/types/employee';

const EPF_STATUS_OPTIONS = [{ value: 'all', label: 'All' }, ...EMPLOYEE_EPF_STATUS_OPTIONS];

type Props = {
  filters: IEmployeeTableFilters;
  onFilters: (name: string, value: IEmployeeTableFilterValue) => void;
  handleResetFilters: VoidFunction;
  canReset: boolean;
  salaryRange: IEmployeeBaseSalaryRange[];
};

export default function EmployeeTableToolbar({
  filters,
  onFilters,
  handleResetFilters,
  canReset,
  salaryRange,
}: Props) {
  const { user: authUser } = useAuthContext();

  const isAdmin = hasPermission(
    authUser,
    'any',
    AuthPermissions.ADMIN_PERMISSION,
    AuthPermissions.SUPER_ADMIN_PERMISSION
  );

  const [input, setInput] = useState<string>(filters.generalSearch);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((value) => {
      onFilters('generalSearch', value);
    }, 500),
    []
  );

  const filterByReceived = useCallback((event: SelectChangeEvent<string>) => {
    onFilters('baseSalary', event.target.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // eslint-disable-next-line arrow-body-style
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleFilterEpf = useCallback(
    (event: SelectChangeEvent<string[]>) => {
      onFilters('epf', event.target.value);
    },
    [onFilters]
  );

  const handleFilterStatus = useCallback(
    (event: SelectChangeEvent<string>) => {
      onFilters('status', event.target.value);
    },
    [onFilters]
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    setInput(value);

    debouncedSearch(value);
  };

  const clearSearchText = () => {
    setInput('');

    debouncedSearch('');
  };

  const clearAll = useCallback(() => {
    setInput('');
    handleResetFilters();
  }, [handleResetFilters]);

  return (
    <Stack
      spacing={2}
      alignItems={{ xs: 'flex-end', md: 'center' }}
      direction={{
        xs: 'column',
        md: 'row',
      }}
      sx={{
        p: 2.5,
        pr: { xs: 2.5, md: 1 },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={2} flexGrow={1} sx={{ width: 1 }}>
        <TextField
          autoFocus
          fullWidth
          value={input}
          onChange={handleSearchChange}
          placeholder="Search for name, NIC, employee number..."
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            ),
            endAdornment:
              input.length > 0 ? (
                <InputAdornment position="end">
                  <IconButton onClick={clearSearchText}>
                    <Iconify icon="eva:close-fill" color="text.disabled" />
                  </IconButton>
                </InputAdornment>
              ) : null,
          }}
          inputProps={{
            autoComplete: 'off',
          }}
        />
      </Stack>

      {isAdmin && (
        <FormControl
          sx={{
            flexShrink: 0,
            width: { xs: 1, md: 180 },
          }}
        >
          <InputLabel>Status</InputLabel>

          <Select
            value={filters.status}
            onChange={handleFilterStatus}
            input={<OutlinedInput label="Status" />}
            renderValue={(selected) => {
              const option = EMPLOYEE_STATUS_OPTIONS.find((item) => item.value === selected);

              return (
                <Label variant="soft" color="primary">
                  {option?.label ?? selected}
                </Label>
              );
            }}
            sx={{ textTransform: 'capitalize' }}
          >
            {EMPLOYEE_STATUS_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <FormControl
        sx={{
          flexShrink: 0,
          width: { xs: 1, md: 180 },
        }}
      >
        <InputLabel>EPF</InputLabel>

        <Select
          multiple={false}
          value={[filters.epf]}
          onChange={handleFilterEpf}
          input={<OutlinedInput label="EPF" />}
          renderValue={(selected) => (
            <Label variant="soft" color="primary">
              {selected}
            </Label>
          )}
          sx={{ textTransform: 'capitalize' }}
        >
          {EPF_STATUS_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl
        sx={{
          flexShrink: 0,
          width: { xs: 1, md: 180 },
        }}
      >
        <InputLabel>Base Salary</InputLabel>

        <Select
          value={filters.baseSalary ? String(filters.baseSalary) : ''}
          onChange={filterByReceived}
          input={<OutlinedInput label="Base Salary" />}
          renderValue={(selected) => (
            <Label variant="soft" color="primary">
              {selected}
            </Label>
          )}
          sx={{ textTransform: 'capitalize' }}
        >
          {salaryRange.map((option) => (
            <MenuItem key={option.baseSalary} value={option.baseSalary}>
              {option.baseSalary}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {canReset && (
        <Button
          color="error"
          onClick={clearAll}
          variant="text"
          sx={{ textDecoration: 'underline' }}
        >
          Clear
        </Button>
      )}
    </Stack>
  );
}
